<template>
  <div class="content-wrapper inventory-query-new">
    <a-spin :spinning="isLoading">
      <div class="query-header">
        <div class="query-card">
          <a-form-model :label-col="{ span: 2 }" :wrapper-col="{ span: 22 }">
            <!-- 品牌选择 -->
            <a-form-model-item>
              <template slot="label">
                <span class="form-label">{{ $t('品牌') }}</span>
              </template>
              <div class="tag-group-container">
                <a-spin :spinning="brandLoading" size="small">
                  <div class="tag-group">
                    <a-checkable-tag
                      v-for="item in brandOptions"
                      :key="item.value"
                      :checked="form.brandCode === item.value"
                      @change="(checked) => handleBrandTagChange(checked, item.value)"
                    >
                      {{ item.label }}
                    </a-checkable-tag>
                    <span v-if="!brandOptions.length && !brandLoading" class="empty-text">
                      {{ $t('暂无品牌数据') }}
                    </span>
                  </div>
                </a-spin>
              </div>
            </a-form-model-item>

            <!-- 型号选择 -->
            <a-form-model-item :label="$t('型号')">
              <a-select
                v-model="form.modelCode"
                show-search
                allow-clear
                :placeholder="$t('请先选择品牌，再选择型号')"
                :disabled="!form.brandCode"
                style="width: 300px"
                @change="handleModelChange"
                :filterOption="filterSelectOption"
              >
                <a-select-option v-for="item in modelOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>

            <!-- 规格模糊选择 -->
            <a-form-model-item v-if="fuzzySpecFields.length" :label="$t('规格过滤')">
              <div class="specs-container">
                <a-row :gutter="[16, 16]">
                  <a-col v-for="f in fuzzySpecFields" :key="f.styleType" :span="5">
                    <div class="spec-item">
                      <div class="spec-label">{{ f.styleType }}</div>
                      <div class="tag-group">
                        <a-checkable-tag
                          v-for="v in f.styleValues"
                          :key="`${f.styleType}:${v}`"
                          :checked="(fuzzySelectedSpecs[f.styleType] || []).includes(v)"
                          @change="(checked) => handleFuzzySpecTagChange(checked, f.styleType, v)"
                        >
                          {{ v }}
                        </a-checkable-tag>
                      </div>
                    </div>
                  </a-col>
                </a-row>
              </div>
            </a-form-model-item>

            <!-- 仓库选择与操作 -->
            <a-form-model-item :label="$t('查询项')">
              <div class="action-bar" @keydown.enter="doQuery(1)">
                <SearchHourse
                  ref="searchHourse"
                  :placeholder="$t('请输入/选择仓库')"
                  @selected="selectedWarehouse"
                  style="width: 240px; margin-right: 16px"
                />
                <a-input
                  v-model.trim="form.imei"
                  :placeholder="$t('请输入IMEI/SN/备注')"
                  style="width: 240px; margin-right: 16px"
                  allow-clear
                />
                <a-select
                  v-model="form.inventoryStatus"
                  allow-clear
                  :placeholder="$t('库存状态')"
                  style="width: 160px; margin-right: 16px"
                >
                  <a-select-option value="CERTIFIED_PRODUCTS">
                    {{ $t('正品') }}
                  </a-select-option>
                  <a-select-option value="DEFECTIVE_PRODUCTS">
                    {{ $t('残次品') }}
                  </a-select-option>
                </a-select>
                <DateRangePicker
                  :placeholder="$t('入库时间')"
                  :startTime="form.startTime"
                  :endTime="form.endTime"
                  format="YYYY-MM-DD"
                  @get_date="onChangeDate"
                  style="width: 400px; margin-right: 16px"
                />
                <a-button
                  type="primary"
                  icon="search"
                  :disabled="
                    !form.modelCode &&
                    !form.warehouseCode &&
                    !form.imei &&
                    !form.startTime &&
                    !form.inventoryStatus
                  "
                  @click="doQuery(1)"
                >
                  {{ $t('库存查询') }}
                </a-button>
                <a-button style="margin-left: 8px" @click="resetSearch">
                  {{ $t('重置') }}
                </a-button>
                <!-- <div v-if="matchHint" class="match-hint">
                  <a-icon type="info-circle" />
                  {{ matchHint }}
                </div> -->
              </div>
            </a-form-model-item>
          </a-form-model>
        </div>
      </div>

      <!-- 搜索结果列表 -->
      <div v-if="showResult" class="results-section">
        <div class="table-card">
          <a-table
            :columns="columns"
            :data-source="tableList"
            :pagination="false"
            :loading="queryLoading"
            row-key="inventoryItemId"
            :scroll="{
              x: 'max-content',
            }"
            bordered
          >
            <template slot="productName" slot-scope="text, record">
              <div class="product-info">
                <div class="sku-name">{{ record.skuName }}</div>
                <div class="barcode">{{ $t('条码') }}: {{ record.barcode }}</div>
              </div>
            </template>
            <template slot="noteSlot" slot-scope="text, record">
              <div v-if="editingNoteId === record.inventoryItemId">
                <a-input
                  v-model="editingNoteText"
                  size="small"
                  style="width: 100%"
                  @pressEnter="saveNote(record)"
                />
                <a-button
                  type="link"
                  size="small"
                  :loading="savingNote"
                  @click="saveNote(record)"
                  style="padding: 0 4px"
                >
                  {{ $t('保存') }}
                </a-button>
                <a-button type="link" size="small" @click="cancelEditNote" style="padding: 0 4px">
                  {{ $t('取消') }}
                </a-button>
              </div>
              <div v-else style="display: flex; align-items: center; gap: 4px">
                <span style="white-space: pre-line">{{ formatNote(text) }}</span>
                <a-icon
                  type="edit"
                  style="cursor: pointer; color: #1890ff"
                  @click="startEditNote(record)"
                />
              </div>
            </template>
            <template slot="specs" slot-scope="text, record">
              <div class="spec-tags">
                <a-tag v-for="(val, key) in record.styleMap" :key="key">
                  {{ val }}
                </a-tag>
              </div>
            </template>
            <template slot="inventoryStatus" slot-scope="text">
              <a-tag :color="getStatusColor(text)">
                {{ getStatusText(text) }}
              </a-tag>
            </template>
            <template slot="warehouse" slot-scope="text, record">
              {{ getWarehouseName(record.warehouseCode) }}
            </template>
            <template slot="unitPrice" slot-scope="text, record">
              <div style="display: flex; align-items: center; justify-content: flex-end; gap: 6px">
                <span>{{ text !== null && text !== undefined && text !== '' ? text : '-' }}</span>
                <a-tooltip :title="$t('修改价格')">
                  <a-icon
                    type="edit"
                    style="cursor: pointer; color: #1890ff"
                    @click="openPriceEditModal(record)"
                  />
                </a-tooltip>
              </div>
            </template>
            <template slot="createdAt" slot-scope="text">
              {{ text || '-' }}
            </template>
            <template slot="storageDuration" slot-scope="text">
              {{ calcStorageDuration(text) }}
            </template>
          </a-table>

          <div class="pagination-footer">
            <div style="display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 12px">
              <div v-if="searchWarehouseName">
                <span style="color: rgba(0, 0, 0, 0.65)">{{ $t('仓库') }}：</span>
                <span>{{ searchWarehouseName }}</span>
              </div>
              <div>
                <span style="color: rgba(0, 0, 0, 0.65)">{{ $t('总实际库存') }}：</span>
                <span>{{ totalRealQuantity ?? 0 }}</span>
              </div>
              <div>
                <span style="color: rgba(0, 0, 0, 0.65)">{{ $t('总可用数') }}：</span>
                <span>{{ totalAvailableQuantity ?? 0 }}</span>
              </div>
              <div>
                <span style="color: rgba(0, 0, 0, 0.65)">{{ $t('总预定订单') }}：</span>
                <span>{{ totalReserveQuantity ?? 0 }}</span>
              </div>
            </div>
            <pagination
              :pageNo="pageNo"
              :length="pageSize"
              :total-count="totalCount"
              @paginate="handlePaginate"
            />
          </div>
        </div>
      </div>
    </a-spin>
    <!-- 备注编辑弹窗 -->
    <a-modal
      :visible="noteEditVisible"
      :title="$t('编辑备注详情')"
      @ok="handleNoteEditOk"
      @cancel="noteEditVisible = false"
      :confirmLoading="savingNote"
      :width="600"
    >
      <a-form-model :model="noteEditForm" :label-col="{ span: 4 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item :label="$t('来源')">
          <a-input v-model="noteEditForm.source" :placeholder="$t('请输入来源')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('串号')">
          <a-input v-model="noteEditForm.imei" :placeholder="$t('请输入串号')" disabled />
        </a-form-model-item>
        <a-form-model-item :label="$t('电池')">
          <a-input v-model="noteEditForm.battery" :placeholder="$t('请输入电池健康度')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('外观')">
          <a-input v-model="noteEditForm.appearance" :placeholder="$t('请输入外观描述')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('备注')">
          <a-textarea v-model="noteEditForm.note" :placeholder="$t('请输入备注')" :rows="3" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 单价编辑弹窗 -->
    <a-modal
      :visible="priceEditVisible"
      :title="$t('修改单价')"
      @ok="handlePriceEditOk"
      @cancel="priceEditVisible = false"
      :confirmLoading="savingPrice"
      :width="550"
      :destroyOnClose="true"
    >
      <a-form-model
        ref="priceEditFormRef"
        :model="priceEditForm"
        :rules="priceEditRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item :label="$t('商品名称')">
          <span>{{ priceEditForm.skuName || '-' }}</span>
        </a-form-model-item>
        <a-form-model-item :label="$t('商品条码')">
          <span>{{ priceEditForm.barcode || '-' }}</span>
        </a-form-model-item>
        <a-form-model-item :label="$t('库存状态')">
          <a-tag :color="getStatusColor(priceEditForm.inventoryStatus)">
            {{ getStatusText(priceEditForm.inventoryStatus) }}
          </a-tag>
        </a-form-model-item>
        <a-form-model-item :label="$t('原单价')">
          <span>
            {{
              priceEditForm.currentUnitPrice !== null &&
              priceEditForm.currentUnitPrice !== undefined &&
              priceEditForm.currentUnitPrice !== ''
                ? priceEditForm.currentUnitPrice
                : '-'
            }}
          </span>
        </a-form-model-item>
        <a-form-model-item :label="$t('新单价')" prop="unitPrice">
          <a-input-number
            ref="priceInputRef"
            v-model="priceEditForm.unitPrice"
            :min="0"
            :precision="2"
            :step="0.01"
            :placeholder="$t('请输入新单价')"
            style="width: 100%"
            autoFocus
            @pressEnter="handlePriceEditOk"
          />
        </a-form-model-item>
        <!-- <a-form-model-item :label="$t('币种')">
          <a-input v-model="priceEditForm.unitCode" :placeholder="$t('例如 CNY')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('业务单据号')">
          <a-input
            v-model="priceEditForm.bizOrderSn"
            :placeholder="$t('请输入业务单据号 (如 ASN...)')"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('出入库类型')">
          <a-input v-model="priceEditForm.transactionType" :placeholder="$t('例如 PURCHASE_IN')" />
        </a-form-model-item> -->
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import http from '@common/http'
import api from '@common/api'
import SearchHourse from '@component/searchHourse'
import pagination from '@component/pagination'
import DateRangePicker from '@/component/dateRangePicker.vue'

const PREFERRED_STYLE_TYPES = ['颜色', '运行内存', '机身存储', '成新', '版本']
const BRAND_CACHE_KEY = 'scm_inventory_brand_options'

export default {
  name: 'inventoryQueryNew',
  components: {
    SearchHourse,
    pagination,
    DateRangePicker,
  },
  data() {
    return {
      isLoading: false,
      brandLoading: false,
      queryLoading: false,
      brandOptions: [],
      modelOptions: [],
      form: {
        brandCode: undefined,
        modelCode: undefined,
        warehouseCode: '',
        warehouseName: '',
        imei: '',
        startTime: '',
        endTime: '',
        inventoryStatus: undefined,
      },
      searchWarehouseName: '',
      skuResult: null,
      fuzzySelectedSpecs: {}, // { styleType: [val1, val2] }
      showResult: false,
      tableList: [],
      pageNo: 1,
      pageSize: 10,
      totalCount: 0,

      // 库存明细
      detailVisible: false,
      detailLoading: false,
      detailList: [],
      detailTotal: 0,
      detailQuery: {
        skuName: '',
        barcode: '',
        warehouseName: '',
        warehouseCode: '',
        pageNo: 1,
        pageSize: 10,
        skuStyleList: [],
      },
      editingNoteId: null,
      editingNoteText: '',
      savingNote: false,
      priceEditVisible: false,
      savingPrice: false,
      priceEditRecord: null,
      priceEditForm: {
        skuName: '',
        barcode: '',
        inventoryStatus: '',
        currentUnitPrice: null,
        unitPrice: null,
        unitCode: 'CNY',
        bizOrderSn: '',
        transactionType: 'PURCHASE_IN',
      },
      priceEditRules: {
        unitPrice: [{ required: true, message: this.$t('请输入新单价'), trigger: 'blur' }],
      },
      colorList: ['pink', 'red', 'orange', 'green', 'cyan', 'blue', 'purple'],

      columns: [
        {
          title: this.$t('商品/条码'),
          dataIndex: 'productName',
          scopedSlots: { customRender: 'productName' },
          width: 400,
        },
        {
          title: this.$t('规格'),
          dataIndex: 'specs',
          scopedSlots: { customRender: 'specs' },
          width: 200,
        },
        {
          title: this.$t('状态'),
          dataIndex: 'inventoryStatus',
          scopedSlots: { customRender: 'inventoryStatus' },
          width: 120,
        },
        {
          title: this.$t('备注'),
          dataIndex: 'note',
          width: 215,
          scopedSlots: { customRender: 'noteSlot' },
        },
        // { title: this.$t('批次内容'), dataIndex: 'batchCode', width: 200 },
        {
          title: this.$t('仓库'),
          dataIndex: 'warehouseCode',
          width: 150,
          scopedSlots: { customRender: 'warehouse' },
        },
        {
          title: this.$t('单价'),
          dataIndex: 'unitPrice',
          width: 120,
          align: 'right',
          scopedSlots: { customRender: 'unitPrice' },
        },
        { title: this.$t('可用库存'), dataIndex: 'availableQuantity', width: 100, align: 'right' },
        { title: this.$t('预定库存'), dataIndex: 'reserveQuantity', width: 100, align: 'right' },
        { title: this.$t('实际库存'), dataIndex: 'realQuantity', width: 100, align: 'right' },
        {
          title: this.$t('入库时间'),
          dataIndex: 'createdAt',
          width: 160,
          scopedSlots: { customRender: 'createdAt' },
        },
        {
          title: this.$t('在库时长'),
          dataIndex: 'createdAt',
          key: 'storageDuration',
          width: 130,
          scopedSlots: { customRender: 'storageDuration' },
        },
      ],
      detailColumns: [
        { title: this.$t('ID'), dataIndex: 'inventoryItemId', width: 80 },
        { title: this.$t('条码'), dataIndex: 'barcode', width: 140 },
        { title: this.$t('状态'), dataIndex: 'inventoryStatus' },
        { title: this.$t('数量'), dataIndex: 'realQuantity', width: 80 },
        { title: this.$t('单价'), dataIndex: 'unitPrice', width: 100 },
        { title: this.$t('备注'), dataIndex: 'note', scopedSlots: { customRender: 'noteSlot' } },
        { title: this.$t('更新时间'), dataIndex: 'lastModifyPriceAt', width: 160 },
      ],
      totalRealQuantity: null,
      totalAvailableQuantity: null,
      totalReserveQuantity: null,
      noteEditVisible: false,
      noteEditForm: {
        source: '',
        imei: '',
        battery: '',
        appearance: '',
        note: '',
      },
      currentRecord: null,
    }
  },
  computed: {
    fuzzySpecFields() {
      if (!this.skuResult?.specOptions) return []
      return this.skuResult.specOptions
        .filter((o) => PREFERRED_STYLE_TYPES.includes(o.styleType))
        .map((o) => ({
          styleType: o.styleType,
          styleValues: Array.isArray(o.styleValues) ? o.styleValues : [],
        }))
    },
    matchedBarcodes() {
      if (!this.skuResult?.skuRelations) return []
      const relations = this.skuResult.skuRelations
      const selected = this.fuzzySelectedSpecs

      return relations
        .filter((r) => {
          const specs = r.specs || []
          // 对每一个已选择的规格大类，SKU 必须符合该大类选中的值之一
          return Object.keys(selected).every((type) => {
            const vals = selected[type] || []
            if (vals.length === 0) return true
            return specs.some((s) => s.styleType === type && vals.includes(s.styleValue))
          })
        })
        .map((r) => r.skuCode)
    },
    matchHint() {
      if (!this.form.modelCode) return ''
      return `当前条件下匹配到 ${this.matchedBarcodes.length} 个SKU条码`
    },
  },
  created() {
    this.fetchBrandList()
  },
  methods: {
    onChangeDate(data) {
      if (data.start_time) {
        this.form.startTime = `${data.start_time} 00:00:00`
        this.form.endTime = `${data.end_time} 23:59:59`
      } else {
        this.form.startTime = undefined
        this.form.endTime = undefined
      }
    },
    // 基础选择逻辑
    async fetchBrandList(force = false) {
      if (!force) {
        const cache = localStorage.getItem(BRAND_CACHE_KEY)
        if (cache) {
          this.brandOptions = JSON.parse(cache)
          return
        }
      }
      this.brandLoading = true
      try {
        const res = await http({ url: api.getUPDBrandList, type: 'post', data: {} })
        const brands = (res?.result?.brandList || []).map((b) => ({
          label: b.brandName,
          value: b.brandId,
          raw: b,
        }))
        this.brandOptions = brands
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brands))
      } finally {
        this.brandLoading = false
      }
    },
    handleRefreshBrandList() {
      this.fetchBrandList(true)
    },
    handleBrandTagChange(checked, brandCode) {
      const next = checked ? brandCode : undefined
      this.form.brandCode = next
      this.form.modelCode = undefined
      this.modelOptions = []
      this.skuResult = null
      this.fuzzySelectedSpecs = {}
      if (next) {
        const brand = this.brandOptions.find((b) => b.value === next)?.raw
        const models = brand?.brandModelList || []
        this.modelOptions = models.map((m) => ({
          label: m.modelName,
          value: m.modelCode,
          raw: m,
        }))
      }
    },
    handleModelChange(modelCode) {
      this.skuResult = null
      this.fuzzySelectedSpecs = {}
      if (!modelCode) return

      const model = this.modelOptions.find((m) => m.value === modelCode)
      this.isLoading = true
      http({
        url: api.querySkuRelationMap,
        type: 'post',
        data: { modelCode, modelName: model.label },
        success: (res) => {
          this.skuResult = res?.result || null
          this.isLoading = false
        },
        fail: () => {
          this.isLoading = false
        },
      })
    },
    handleFuzzySpecTagChange(checked, type, value) {
      const next = { ...this.fuzzySelectedSpecs }
      const current = next[type] || []
      if (checked) {
        if (!current.includes(value)) current.push(value)
      } else {
        const idx = current.indexOf(value)
        if (idx > -1) current.splice(idx, 1)
      }
      this.$set(this.fuzzySelectedSpecs, type, current)
    },
    selectedWarehouse(data) {
      this.form.warehouseCode = data.warehouseCode || ''
      this.form.warehouseName = data.warehouseName || ''
    },

    // 查询逻辑
    async doQuery(pageNo = 1) {
      this.pageNo = pageNo
      this.queryLoading = true
      this.showResult = true

      try {
        const barcodes = this.matchedBarcodes.length > 0 ? this.matchedBarcodes : null

        if (
          !barcodes &&
          !this.form.warehouseCode &&
          !this.form.imei &&
          !this.form.startTime &&
          !this.form.inventoryStatus
        ) {
          this.$message.warning(this.$t('请选择查询条件'))
          this.queryLoading = false
          return
        }

        const res = await http({
          url: api.stockListSimple,
          type: 'post',
          data: {
            barcodeList: barcodes,
            pageNo: this.pageNo,
            pageSize: this.pageSize,
            ...this.form,
          },
        })

        this.tableList = res?.result?.list || []
        this.totalCount = res?.result?.total || 0
        this.totalRealQuantity = res?.result?.totalRealQuantity || 0
        this.totalAvailableQuantity = res?.result?.totalAvailableQuantity || 0
        this.totalReserveQuantity = res?.result?.totalReserveQuantity || 0

        this.searchWarehouseName = this.form.warehouseName
      } finally {
        this.queryLoading = false
      }
    },
    handlePaginate(data) {
      this.pageNo = data.pageNo
      this.pageSize = data.pageSize
      this.doQuery(this.pageNo)
    },
    resetSearch() {
      this.form = {
        brandCode: undefined,
        modelCode: undefined,
        warehouseCode: '',
        imei: '',
        startTime: '',
        endTime: '',
        inventoryStatus: undefined,
      }
      this.modelOptions = []
      this.skuResult = null
      this.fuzzySelectedSpecs = {}
      this.showResult = false
      this.tableList = []
    },

    // 表格辅助
    getStatusColor(status) {
      const maps = {
        GOODS: 'green',
        DEFECTIVE_PRODUCTS: 'red',
        CERTIFIED_PRODUCTS: 'blue',
      }
      return maps[status] || 'orange'
    },
    getStatusText(status) {
      const maps = {
        GOODS: this.$t('良品'),
        DEFECTIVE_PRODUCTS: this.$t('残次品'),
        CERTIFIED_PRODUCTS: this.$t('正品'),
      }
      return maps[status] || status
    },
    filterSelectOption(input, option) {
      return (option.componentOptions?.children?.[0]?.text || '')
        .toLowerCase()
        .includes(input.toLowerCase())
    },
    startEditNote(record) {
      const text = record.note || ''
      let isSpecial = false
      try {
        const obj = JSON.parse(text)
        if (obj && typeof obj === 'object') {
          this.noteEditForm = {
            source: obj.source || '',
            imei: obj.imei || '',
            battery: obj.battery || '',
            appearance: obj.appearance || '',
            note: obj.note || '',
          }
          isSpecial = true
        }
      } catch (e) {
        // Check for Source [IMEI] format: 采购入库 ["111122"]
        const match = text.match(/^(.*)\s+(\[.*\])$/)
        if (match) {
          this.noteEditForm = {
            source: match[1].trim(),
            imei: match[2],
            battery: '',
            appearance: '',
            note: '',
          }
          isSpecial = true
        }
      }

      if (isSpecial) {
        this.currentRecord = record
        this.noteEditVisible = true
      } else {
        this.editingNoteId = record.inventoryItemId
        this.editingNoteText = text
      }
    },
    cancelEditNote() {
      this.editingNoteId = null
      this.editingNoteText = ''
      this.currentRecord = null
    },
    async saveNote(record) {
      this.savingNote = true
      try {
        await http({
          url: api.updateInventoryItemNote,
          type: 'post',
          data: { inventoryItemId: record.inventoryItemId, note: this.editingNoteText },
        })
        record.note = this.editingNoteText
        this.cancelEditNote()
        this.$message.success('备注更新成功')
      } finally {
        this.savingNote = false
      }
    },
    async handleNoteEditOk() {
      if (!this.currentRecord) return
      this.savingNote = true
      try {
        const note = JSON.stringify(this.noteEditForm)
        await http({
          url: api.updateInventoryItemNote,
          type: 'post',
          data: { inventoryItemId: this.currentRecord.inventoryItemId, note },
        })
        this.currentRecord.note = note
        this.noteEditVisible = false
        this.$message.success('备注更新成功')
      } catch (e) {
        this.$message.error('备注更新失败')
      } finally {
        this.savingNote = false
      }
    },
    formatNote(text) {
      if (!text) return text
      try {
        const obj = JSON.parse(text)
        if (obj && typeof obj === 'object') {
          let imei = obj.imei
          if (typeof imei === 'string' && imei.startsWith('[')) {
            try {
              const imeiArr = JSON.parse(imei)
              imei = Array.isArray(imeiArr) ? imeiArr.join(',') : imei
            } catch (e) {
              console.log(e)
            }
          }
          const parts = []
          if (obj.source) parts.push(`${this.$t('来源')}: ${obj.source}`)
          if (imei) parts.push(`${this.$t('串码')}: [${imei}]`)
          if (obj.battery) parts.push(`${this.$t('电池')}: ${obj.battery}`)
          if (obj.appearance) parts.push(`${this.$t('外观')}: ${obj.appearance}`)
          if (obj.note) parts.push(`${this.$t('备注')}: ${obj.note}`)

          if (parts.length > 0) {
            return parts.join('\n')
          }
        }
      } catch (e) {
        // console.log(e)
      }
      return text
    },
    getWarehouseName(warehouseCode) {
      if (!warehouseCode) return '-'
      const list = this.$refs.searchHourse?.warehouseList || []
      const target = list.find((item) => item.warehouseCode === warehouseCode)
      return target ? target.warehouseName : warehouseCode
    },
    calcStorageDuration(createdAt) {
      if (!createdAt) return '-'
      const start = new Date(createdAt)
      if (isNaN(start.getTime())) return '-'
      const now = new Date()
      const diffMs = now - start
      if (diffMs < 0) return '-'
      const totalHours = Math.floor(diffMs / (1000 * 60 * 60))
      const days = Math.floor(totalHours / 24)
      const hours = totalHours % 24
      if (days === 0) return `${hours}${this.$t('小时')}`
      return `${days}${this.$t('天')}${hours}${this.$t('小时')}`
    },
    openPriceEditModal(record) {
      this.priceEditRecord = record
      this.priceEditForm = {
        skuName: record.skuName || record.productName || '',
        barcode: record.barcode || '',
        inventoryStatus: record.inventoryStatus || '',
        currentUnitPrice: record.unitPrice,
        unitPrice: '',
        unitCode: record.unitCode || 'CNY',
        bizOrderSn: record.bizOrderSn || '',
        transactionType: record.transactionType || 'PURCHASE_IN',
      }
      this.priceEditVisible = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.priceInputRef?.focus?.()
        }, 100)
      })
    },
    async handlePriceEditOk() {
      this.$refs.priceEditFormRef.validate(async (valid) => {
        if (!valid) return
        if (!this.priceEditRecord) return

        this.savingPrice = true
        try {
          const payload = {
            merchantCode: localStorage.getItem('merchantCode'),
            list: [
              {
                // bizOrderSn: this.priceEditForm.bizOrderSn,
                // transactionType: this.priceEditForm.transactionType || 'PURCHASE_IN',
                changeList: [
                  {
                    barcode: this.priceEditForm.barcode,
                    // inventoryStatus: this.priceEditForm.inventoryStatus,
                    unitPrice: Number(this.priceEditForm.unitPrice),
                    // unitCode: this.priceEditForm.unitCode || 'CNY',
                  },
                ],
              },
            ],
          }

          const res = await http({
            url: api.updateInventoryItemPrice,
            type: 'post',
            data: payload,
          })

          if (res) {
            this.priceEditRecord.unitPrice = this.priceEditForm.unitPrice
            if (this.priceEditForm.unitCode) {
              this.priceEditRecord.unitCode = this.priceEditForm.unitCode
            }
            if (this.priceEditForm.bizOrderSn) {
              this.priceEditRecord.bizOrderSn = this.priceEditForm.bizOrderSn
            }
            if (this.priceEditForm.transactionType) {
              this.priceEditRecord.transactionType = this.priceEditForm.transactionType
            }

            this.priceEditVisible = false
            this.$message.success(this.$t('单价修改成功'))
          }
        } catch (e) {
          console.error('updateInventoryItemPrice error:', e)
          this.$message.error(e?.msg || e?.message || this.$t('单价修改失败'))
        } finally {
          this.savingPrice = false
        }
      })
    },
  },
}
</script>

<style scoped>
.inventory-query-new {
  background: #f0f2f5;
  min-height: calc(100vh - 140px);
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
  color: rgba(0, 0, 0, 0.85);
}
.query-card,
.table-card {
  background: #fff;
  /* padding: 24px; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.query-card {
  padding-top: 10px;
  padding-bottom: 1px;
}
.table-card {
  padding-bottom: 10px;
}
.form-label {
  font-weight: 500;
}
.refresh-icon {
  margin-left: 8px;
  color: #1890ff;
  cursor: pointer;
  transition: transform 0.3s;
}
.refresh-icon:hover {
  transform: rotate(180deg);
}
.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.empty-text {
  color: rgba(0, 0, 0, 0.45);
}
.specs-container {
  background: #fafafa;
  padding: 0 16px;
  border-radius: 4px;
}
.spec-item {
  margin-bottom: 8px;
}
.spec-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
.action-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.match-hint {
  margin-left: 16px;
  color: #1890ff;
  font-size: 13px;
}
.results-section {
  margin-top: 12px;
}
.product-info .sku-name {
  font-weight: 500;
}
.product-info .barcode {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.spec-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}
.pagination-footer {
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
}
.detail-header {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
}
.detail-item .label {
  color: rgba(0, 0, 0, 0.45);
}
.detail-item .value {
  font-weight: 500;
}
.note-edit-box,
.note-display-box {
  display: flex;
  align-items: center;
  gap: 8px;
}
.edit-icon {
  color: #1890ff;
  cursor: pointer;
}
.mt16 {
  margin-top: 16px;
}

.ant-tag-checkable {
  cursor: pointer;
}

.query-card :deep(.ant-row) {
  display: flex;
  align-items: center;
}

.ant-form-item {
  margin-bottom: 12px;
}
</style>
